import { graphql } from "gatsby";
import React from "react";
import { SwiperSlide } from "swiper/react";
import { twJoin } from "tailwind-merge";

import Button from "../button";
import ProductTeaser from "../product/teaser";
import RichText from "../richText";
import OverviewSlider from "../slider";

type Props = {
  slice:
    | Queries.HomePopularProductsSliceFragment
    | Queries.TreatmentsOverviewPopularProductSliceFragment
    | Queries.ProductPopularProductsSliceFragment;
  bordered: boolean;
};
const PopularProducts: React.FC<Props> = ({ slice, bordered = false }) => {
  const { primary, items } = slice;

  const listOfProducts = items.map((slice, index) => {
    const product = { uid: null, lang: null, type: null, ...slice?.product?.document };
    if (!product.uid || !product.lang || !product.type || !product.data || !product.shopify) return null;

    return (
      <SwiperSlide key={index} virtualIndex={index} className="z-10 !h-auto max-w-[315px]">
        <ProductTeaser result={product} useCase="singleProductPage" className="h-full" />
      </SwiperSlide>
    );
  });

  return (
    <section className={twJoin("mx-auto max-w-screen-max", bordered && "border-y border-y-black/10")}>
      <section className="col-span-full gap-0 mx-5 max-w-screen-xl md:mx-11 lg:mx-24 xl:mx-auto p-t-xxxl-fluid baseGrid lg:gap-x-lg">
        <div className="col-span-full lg:col-span-12 m-b-lg-fluid">
          <RichText data={primary.title?.richText} className="block mb-2 font-medium" />
          <RichText data={primary.lead?.richText} className="block typo-base text-black/70" />
        </div>
        {primary.buttonTarget && primary.buttonText?.richText.length > 0 && (
          <div className="p-b-lg-fluid m-t-lg-fluid col-span-full hidden lg:col-span-12 lg:row-start-auto lg:!mt-0 lg:flex lg:items-end lg:justify-end">
            <Button link={primary.buttonTarget} type="secondary">
              <RichText data={primary.buttonText?.richText} />
            </Button>
          </div>
        )}
      </section>
      <section className="col-span-full gap-0 xl:mx-auto p-b-xxxl-fluid baseGrid lg:gap-x-lg">
        {listOfProducts.length > 0 && <OverviewSlider slides={listOfProducts} type="products" initialSlides={4} />}
        {primary.buttonTarget && primary.buttonText?.richText.length > 0 && (
          <div className="flex col-span-full justify-center items-center lg:hidden m-t-lg-fluid">
            <Button link={primary.buttonTarget} type="secondary">
              <RichText data={primary.buttonText?.richText} />
            </Button>
          </div>
        )}
      </section>
    </section>
  );
};

export const query = graphql`
  fragment HomePopularProductsSlice on PrismicPageHomeDataBodyPopularProducts {
    ...Slice
    primary {
      title {
        ...RichText
      }
      lead {
        ...RichText
      }
      buttonText: button_text {
        ...RichText
      }
      buttonTarget: button_target {
        ...Link
      }
    }
    items {
      product {
        document {
          ... on PrismicProduct {
            ...ProductTeaser
          }
        }
      }
    }
  }

  fragment TreatmentsOverviewPopularProductSlice on PrismicPageTreatmentsOverviewDataBodyPopularProducts {
    ...Slice
    primary {
      title {
        ...RichText
      }
      lead {
        ...RichText
      }
      buttonText: button_text {
        ...RichText
      }
      buttonTarget: button_target {
        ...Link
      }
    }
    items {
      product {
        ...Link
        document {
          ... on PrismicProduct {
            ...ProductTeaser
          }
        }
      }
    }
  }

  fragment ProductPopularProductsSlice on PrismicProductDataBodyPopularProducts {
    ...Slice
    primary {
      title {
        ...RichText
      }
      lead {
        ...RichText
      }
      buttonText: button_text {
        ...RichText
      }
      buttonTarget: button_target {
        ...Link
      }
    }
    items {
      product {
        document {
          ... on PrismicProduct {
            ...ProductTeaser
          }
        }
      }
    }
  }

  fragment CartPopularProductsSlice on PrismicPageCartDataBodyPopularProducts {
    ...Slice
    primary {
      title {
        ...RichText
      }
      lead {
        ...RichText
      }
      buttonText: button_text {
        ...RichText
      }
      buttonTarget: button_target {
        ...Link
      }
    }
    items {
      product {
        document {
          ... on PrismicProduct {
            ...ProductTeaser
          }
        }
      }
    }
  }
`;

export default PopularProducts;
